import { authService } from "@/services/authService";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useAuth from "@/hooks/stores/useAuth";
import {
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  User,
  User2Icon,
  Users2,
  UsersIcon,
  SettingsIcon,
  LogOut,
  MessageSquare,
  ListChecks,
  User2,
  Users,
  Calendar,
  PieChart,
  Mail,
  Phone,
  Send,
  X,
  Menu,
  FileTextIcon,
  Flag,
} from "lucide-react";
import dashboardIcon from "@/assets/icons/dashboard.svg";
import interSlotsIcon from "@/assets/icons/inter_slots.svg";
import applicationsIcon from "@/assets/icons/applications.svg";
import homeLineIcon from "@/assets/icons/home-line.svg";
import rowsIcon from "@/assets/icons/rows-01.svg";
import kernelLogo from "@/assets/icons/kernel_logo.png";
import pageBgWrapIcon from "@/assets/icons/Page background wrap.svg";
import useGlobalStore from "@/hooks/stores/useGlobalStore";
import DataLossWarningDialog from "./shared/DataLossWarningDialog";
import { campaignsService } from "@/services/campaignsService";
import Cookies from "js-cookie";
import Loader from "./Loader";
import { notificationService } from "@/services/notificationService";
import useNotifications from "@/hooks/useNotifications";
import { useCandidateContactRequestChannels } from "@/hooks/useCandidateContactRequestChannels";
import NotificationBell from "./NotificationBell";

const Sidebar = ({ sidebarToggleAction }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userRole, currentUser } = useAuth();
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const {
    showDataLossWarning,
    setDataLossWarning,
    hasUnreadNotifications,
    setHasUnreadNotifications,
  } = useGlobalStore();
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() =>
    typeof window !== "undefined"
      ? !window.matchMedia("(max-width: 767px)").matches
      : true,
  );
  const [companyBrandColor, setCompanyBrandColor] = useState(null);

  // Fetch company brand color when company user is on dashboard
  const rawData = useAuth((state) => state.getRawData()); //auth.companies;

  useEffect(() => {
    const fetchCompanyBrandColor = async () => {
      // Only fetch for company users and when on dashboard
      if (
        userRole?.toLowerCase() === "company" &&
        location.pathname === "/dashboard"
      ) {
        try {
          // const rawData = Cookies.get('rawData');

          if (rawData) {
            // const parsedData = JSON.parse(atob(rawData));
            const companyId = rawData?.user?.company_logins?.[0]?.company_id;

            if (companyId) {
              const campaignsResponse =
                await campaignsService.getCampaignByCompanyId(companyId);
              const company = campaignsResponse?.campaigns?.[0]?.company;
              if (company?.brand_color) {
                setCompanyBrandColor(company.brand_color);
              }
            }
          }
        } catch (error) {
          console.error("Error fetching company brand color:", error);
        }
      } else {
        // Reset brand color when not on dashboard or not company user
        setCompanyBrandColor(null);
      }
    };

    fetchCompanyBrandColor();
  }, [userRole, location.pathname]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onBreakpointChange = () => {
      if (mq.matches) {
        setIsSidebarOpen(false);
        sidebarToggleAction(false);
      } else {
        setIsSidebarOpen(true);
        sidebarToggleAction(true);
      }
    };
    mq.addEventListener("change", onBreakpointChange);
    return () => mq.removeEventListener("change", onBreakpointChange);
  }, [sidebarToggleAction]);

  useEffect(() => {
    if (!window.matchMedia("(max-width: 767px)").matches) return;
    setIsSidebarOpen(false);
    sidebarToggleAction(false);
  }, [location.pathname, sidebarToggleAction]);

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => {
      const next = !prev;
      sidebarToggleAction(next);
      return next;
    });
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
    sidebarToggleAction(false);
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleNavigation = (path, action) => {
    if (showDataLossWarning) {
      setPendingNavigation({ path, action });
      setShowWarningDialog(true);
    } else {
      action ? action() : navigate(path);
    }
  };

  const homeHref =
    userRole?.toLowerCase() === "company"
      ? "/dashboard"
      : userRole?.toLowerCase() === "candidate"
        ? "/candidate/dashboard"
        : ["admin", "company_admin"].includes(userRole?.toLowerCase() ?? "")
          ? "/admin-dashboard"
          : "/";

  const navigateHome = (e) => {
    e.preventDefault();
    const path =
      userRole?.toLowerCase() === "company"
        ? "/dashboard"
        : userRole?.toLowerCase() === "candidate"
          ? "/candidate/dashboard"
          : ["admin", "company_admin"].includes(userRole?.toLowerCase() ?? "")
            ? "/admin-dashboard"
            : "/";
    handleNavigation(path);
  };

  const handleConfirmNavigation = () => {
    if (pendingNavigation) {
      const { path, action } = pendingNavigation;
      // Reset data loss warning state before navigating
      setDataLossWarning(false);
      action ? action() : navigate(path);
      setShowWarningDialog(false);
      setPendingNavigation(null);
    }
  };

  const handleDiscardAndNavigate = () => {
    if (pendingNavigation) {
      const { path, action } = pendingNavigation;
      // Reset data loss warning state and navigate
      setDataLossWarning(false);
      action ? action() : navigate(path);
      setShowWarningDialog(false);
      setPendingNavigation(null);
    }
  };

  const handleCancelNavigation = () => {
    setShowWarningDialog(false);
    setPendingNavigation(null);
  };

  // Add this useEffect to set a class on the body element
  useEffect(() => {
    document.body.classList.toggle("sidebar-collapsed", !isSidebarOpen);
    return () => {
      document.body.classList.remove("sidebar-collapsed");
    };
  }, [isSidebarOpen]);

  // Real-time notifications
  useNotifications();
  useCandidateContactRequestChannels();

  // Fetch unread state on mount, on refresh, and when route changes so the bell dot is correct on every page
  useEffect(() => {
    const checkNotifications = async () => {
      try {
        if (["admin", "recruiter"].includes(userRole?.toLowerCase())) {
          const data = await notificationService.getNotifications();
          const notificationsList = Array.isArray(data)
            ? data
            : data.data || [];
          const hasUnread = notificationsList.some(
            (n) => n.status === "unread" || n.read === false,
          );
          setHasUnreadNotifications(hasUnread);
        } else if (userRole?.toLowerCase() !== "candidate") {
          setHasUnreadNotifications(false);
        }
        /* candidate: unread bell is driven by Pusher (useCandidateContactRequestChannels / useNotifications) */
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
      }
    };

    if (userRole) {
      checkNotifications();
    }
  }, [userRole, setHasUnreadNotifications, location.pathname]);

  // Show loader overlay if company user and companyBrandColor is not loaded yet
  if (userRole?.toLowerCase() === "company" && !companyBrandColor) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          background: "rgba(255,255,255,0.85)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loader loadingText="Loading sidebar..." />
      </div>
    );
  }

  const brandButtonStyle =
    userRole?.toLowerCase() === "company" && companyBrandColor
      ? { backgroundColor: companyBrandColor }
      : {};

  const iconButtonClass =
    "flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-card text-foreground border border-border shadow-sm transition-colors";

  return (
    <>
      {/* Mobile (drawer closed): K + hamburger fixed top-left — X lives inside sidebar when open */}
      {!isSidebarOpen && (
        <div className="fixed top-6 left-4 z-50 flex md:hidden flex-col items-center gap-2">
          <a
            href={homeHref}
            onClick={navigateHome}
            className={`${iconButtonClass} text-xl font-bold`}
            style={brandButtonStyle}
          >
            K
          </a>
          <button
            type="button"
            aria-expanded={false}
            aria-label="Open menu"
            onClick={toggleSidebar}
            className={iconButtonClass}
            style={brandButtonStyle}
          >
            <Menu className="h-6 w-6 text-foreground" strokeWidth={2} />
          </button>
        </div>
      )}

      {/* Desktop: logo toggle + appTrkr when open */}
      <div className="hidden md:flex fixed top-6 left-6 z-50 items-center gap-2 transition-all duration-300">
        <button
          type="button"
          onClick={toggleSidebar}
          aria-expanded={isSidebarOpen}
          aria-label="Toggle sidebar"
          className="pl-1 rounded-md bg-card text-foreground border border-border transition-colors shadow-sm"
          style={brandButtonStyle}
        >
          <img
            src={kernelLogo}
            alt=""
            className="block h-8 w-auto dark:invert"
            width={18}
            height={18}
          />
        </button>
        {isSidebarOpen && (
          <a
            href={homeHref}
            className="text-foreground text-2xl font-bold mb-[-12px]"
            style={
              userRole?.toLowerCase() === "company" && companyBrandColor
                ? {
                    backgroundColor: companyBrandColor,
                    borderRadius: "8px",
                    padding: "4px 12px",
                    color: "#000",
                  }
                : {}
            }
            onClick={navigateHome}
          >
            appTrkr
          </a>
        )}
      </div>

      {isSidebarOpen && (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-30 bg-black/40 md:hidden cursor-pointer border-0 p-0"
          onClick={closeSidebar}
        />
      )}

      <aside
        className={`fixed z-40 left-0 top-0 w-72 max-w-[85vw] border-r border-border overflow-y-auto h-full transition-all duration-300 text-foreground ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} ${!companyBrandColor ? "bg-card" : ""}`}
        style={companyBrandColor ? { backgroundColor: companyBrandColor } : {}}
      >
        <div className="flex flex-col h-full">
          <div className="flex md:hidden shrink-0 items-center justify-between gap-3 px-4 pt-5 pb-3 border-b border-border">
            <a
              href={homeHref}
              onClick={navigateHome}
              className={`${iconButtonClass} text-xl font-bold`}
            >
              K
            </a>
            <button
              type="button"
              aria-label="Close menu"
              onClick={closeSidebar}
              className={iconButtonClass}
            >
              <X className="h-6 w-6 text-foreground" strokeWidth={2} />
            </button>
          </div>

          <div className="p-10 pb-5 md:block hidden">
            {/* Remove the App TRKR text from here */}
          </div>

          <div className="flex-1 overflow-y-auto pt-2 md:pt-5">
            <nav className="px-[25px]">
              <ul>
                {userRole.toLowerCase() === "company" ? (
                  <NavItem
                    link="/dashboard"
                    icon={<SidebarAssetIcon src={dashboardIcon} />}
                    menu="Dashboard"
                    onClick={() => {
                      const userCompanySlug =
                        rawData?.user?.company_logins?.[0]?.company_slug;
                      const dashboardPath = userCompanySlug
                        ? `/dashboard/${userCompanySlug}`
                        : "/dashboard";
                      handleNavigation(dashboardPath);
                    }}
                    showDataLossWarning={showDataLossWarning}
                    brandColor={companyBrandColor}
                  />
                ) : userRole.toLowerCase() === "candidate" ? (
                  <>
                    <NavItem
                      link="/candidate/dashboard"
                      icon={<SidebarAssetIcon src={dashboardIcon} />}
                      menu="Dashboard"
                      onClick={() => handleNavigation("/candidate/dashboard")}
                      showDataLossWarning={showDataLossWarning}
                    />
                    <NavItem
                      link="/candidate/applications"
                      icon={<SidebarAssetIcon src={applicationsIcon} />}
                      menu="Applications"
                      onClick={() =>
                        handleNavigation("/candidate/applications")
                      }
                      showDataLossWarning={showDataLossWarning}
                    />
                  </>
                ) : (
                  <>
                    <NavItem
                      link="/companies"
                      icon={<CompaniesIcon />}
                      menu="Companies"
                      onClick={() => handleNavigation("/companies")}
                      showDataLossWarning={showDataLossWarning}
                    />


                    

                    {["admin", "company_admin"].includes(
                      userRole.toLowerCase(),
                    ) && (
                      <>
                        <NavItem
                          link="/forms"
                          icon={<FormIcon />}
                          menu="Forms"
                          onClick={() => handleNavigation("/forms")}
                          showDataLossWarning={showDataLossWarning}
                        />
                        <NavItem
                          link="/recruiters"
                          icon={
                            <Users2
                              width={18}
                              height={18}
                              className="text-muted-foreground"
                            />
                          }
                          menu="Recruiters"
                          onClick={() => handleNavigation("/recruiters")}
                          showDataLossWarning={showDataLossWarning}
                        />
                      </>
                    )}
                    {["admin", "company_admin", "recruiter"].includes(
                      userRole.toLowerCase(),
                    ) && (
                      <>
                        {["admin"].includes(userRole.toLowerCase()) && (
                          <NavItem
                            link="/vacancy/Statuses"
                            icon={<ListChecks />}
                            menu="Vacancy Statuses"
                            onClick={() => handleNavigation("/vacancy/Statuses")}
                            showDataLossWarning={showDataLossWarning}
                          />
                        )}
                        <NavItem
                          link="/note-templates"
                          icon={<FileTextIcon />}
                          menu="Note Templates"
                          onClick={() => handleNavigation("/note-templates")}
                          showDataLossWarning={showDataLossWarning}
                        />
                      </>
                    )}
                    <NavItem
                      link="/candidate"
                      icon={
                        <Users
                          width={18}
                          height={18}
                          className="text-muted-foreground"
                        />
                      }
                      menu="Candidates"
                      onClick={() => handleNavigation("/candidate")}
                      showDataLossWarning={showDataLossWarning}
                    />
                  
                    {/* Archive Dropdown */}
                    <li className="relative ">
                      <button
                        onClick={() =>
                          openSubmenu == "Campaign"
                            ? setOpenSubmenu(null)
                            : setOpenSubmenu("Campaign")
                        }
                        className={`relative flex w-full items-center rounded py-[8px] my-[2px] px-[15px] text-base font-medium text-foreground hover:bg-muted transition-colors ${openSubmenu == "Campaign" ? "bg-muted" : ""}`}
                      >
                        <span className="mr-[10px] text-muted-foreground flex items-center justify-center">
                          <CampaignsIcon />
                        </span>
                        Campaigns
                        {openSubmenu == "Campaign" ? (
                          <ChevronUp className="ml-auto h-5 w-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="ml-auto h-5 w-5 text-muted-foreground" />
                        )}
                      </button>
                      {openSubmenu == "Campaign" && (
                        <ul className="ml-6 mt-2 space-y-1">
                          <DropdownItem
                            link="/all/campaigns"
                            menu="All "
                            onClick={() => handleNavigation("/all/campaigns")}
                          />
                          <DropdownItem
                            link="/archived/campaigns"
                            menu="Archived "
                            onClick={() =>
                              handleNavigation("/archived/campaigns")
                            }
                          />
                        </ul>
                      )}
                    </li>

                    <li className="relative ">
                      <button
                        onClick={() =>
                          openSubmenu == "Vacancy"
                            ? setOpenSubmenu(null)
                            : setOpenSubmenu("Vacancy")
                        }
                        className={`relative flex w-full items-center rounded py-[8px] my-[2px] px-[15px] text-base font-medium text-foreground hover:bg-muted transition-colors ${openSubmenu == "Vacancy" ? "bg-muted" : ""}`}
                      >
                        <span className="mr-[10px] text-muted-foreground flex items-center justify-center">
                          <VacanciesIcon />
                        </span>
                        Vacancies
                        {openSubmenu == "Vacancy" ? (
                          <ChevronUp className="ml-auto h-5 w-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="ml-auto h-5 w-5 text-muted-foreground" />
                        )}
                      </button>

                      {openSubmenu == "Vacancy" && (
                        <ul className="ml-6 mt-2 space-y-1">
                          <DropdownItem
                            link="/all/vacancies"
                            menu=" All "
                            onClick={() => handleNavigation("/all/vacancies")}
                          />
                          <DropdownItem
                            link="/archived/vacancies"
                            menu=" Archived "
                            onClick={() =>
                              handleNavigation("/archived/vacancies")
                            }
                          />
                        </ul>
                      )}
                    </li>

                    {["admin", "company_admin"].includes(
                      userRole.toLowerCase(),
                    ) && (
                      <>
                        <NavItem
                          link="/admin-dashboard"
                          icon={
                            <SettingsIcon
                              style={{ height: 18, width: 18 }}
                              className="text-muted-foreground"
                            />
                          }
                          menu="Client Dashboard"
                          onClick={() => handleNavigation("/admin-dashboard")}
                        />
                        <NavItem
                          link="/users"
                          icon={
                            <UsersIcon
                              width={18}
                              height={18}
                              className="text-muted-foreground"
                            />
                          }
                          menu="Users"
                          onClick={() => handleNavigation("/users")}
                        />
                      </>
                    )}
                    {userRole.toLowerCase() !== "recruiter" && (
                      <NavItem
                        link="/notes"
                        icon={
                          <MessageSquare
                            width={18}
                            height={18}
                            className="text-muted-foreground"
                          />
                        }
                        menu="Notes"
                        onClick={() => handleNavigation("/notes")}
                      />
                    )}
                    
                    {/* {userRole.toLowerCase() == "recruiter" && (
                      <NavItem
                        link="/interviews-slots"
                        icon={<MessageSquare width={18} height={18} />}
                        menu="Interviews Slots"
                        onClick={() => handleNavigation("/interviews-slots")}
                      />
                      
                    )} */}

                    {["admin", "recruiter"].includes(
                      userRole?.toLowerCase(),
                    ) && (
                      <li className="relative">
                        <button
                          onClick={() =>
                            openSubmenu === "Interview"
                              ? setOpenSubmenu(null)
                              : setOpenSubmenu("Interview")
                          }
                          className={`relative flex w-full items-center rounded py-[8px] my-[2px] px-[15px] text-base font-medium text-foreground hover:bg-muted transition-colors ${openSubmenu === "Interview" ? "bg-muted" : ""}`}
                        >
                          <span className="mr-[10px] text-muted-foreground flex items-center justify-center">
                            <Calendar
                              width={18}
                              height={18}
                              className="text-muted-foreground"
                            />
                          </span>
                          Interview Slots
                          {openSubmenu === "Interview" ? (
                            <ChevronUp className="ml-auto h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="ml-auto h-5 w-5 text-muted-foreground" />
                          )}
                        </button>

                        {openSubmenu === "Interview" && (
                          <ul className="ml-6 mt-2 space-y-1">
                            <DropdownItem
                              link="/interviews-slots"
                              menu="Event Slots"
                              onClick={() =>
                                handleNavigation("/interviews-slots")
                              }
                            />
                            <DropdownItem
                              link="/contact-requests"
                              menu="Contact Requests"
                              onClick={() =>
                                handleNavigation("/contact-requests")
                              }
                            />
                          </ul>
                        )}
                      </li>
                    )}

                    {/* settings  just for admin */}
                    {userRole.toLowerCase() === "admin" && (
                      <>
                        <NavItem
                          link="/marketing"
                          icon={
                            <Send
                              width={18}
                              height={18}
                              className="text-[#A3A7AE]"
                            />
                          }
                          menu="Marketing"
                          onClick={() => handleNavigation("/marketing")}
                        />
                        <NavItem
                          link="/settings"
                          icon={
                            <SettingsIcon
                              style={{ height: 18, width: 18 }}
                              className="text-[#A3A7AE]"
                            />
                          }
                          menu="Settings"
                          onClick={() => handleNavigation("/settings")}
                        />
                      </>
                    )}
                    {["admin", "company_admin", "recruiter"].includes(
                      userRole?.toLowerCase(),
                    ) && (
                      <NavItem
                        link="/recruiter-admin-dashboard"
                        icon={
                          <PieChart
                            width={18}
                            height={18}
                            className="text-muted-foreground"
                          />
                        }
                        menu="Analytics"
                        onClick={() =>
                          handleNavigation("/recruiter-admin-dashboard")
                        }
                        showDataLossWarning={showDataLossWarning}
                        brandColor={companyBrandColor}
                      />

                      
                    )}
                      <NavItem
                      link="/foundation-members"
                      icon={
                        <Flag
                          width={18}
                          height={18}
                          className="text-muted-foreground"
                        />
                      }
                      menu="Foundation Report"
                      onClick={() => handleNavigation("/foundation-members")}
                      showDataLossWarning={showDataLossWarning}
                    />
                  </>
                )}
              </ul>
            </nav>
          </div>

          {/* User Profile Section */}
          {(currentUser?.name || currentUser?.email || rawData?.user?.name) && (
            <div className="mx-6 mb-2">
              <div className="flex flex-col bg-card rounded-xl shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] outline outline-1 outline-border p-3 gap-0.5">
                <div className="text-foreground text-sm font-semibold truncate px-1">
                  {currentUser?.name || rawData?.user?.name || "User Name"}
                </div>
                <div className="text-muted-foreground text-xs font-normal truncate px-1">
                  {currentUser?.email ||
                    rawData?.user?.email ||
                    "user@example.com"}
                </div>
              </div>
            </div>
          )}

          {/* Logout section at bottom */}
          <div className="px-[25px] py-6 flex items-center gap-1 justify-between border-t border-border">
            <NavItem
              onClick={() => handleNavigation("/#", handleLogout)}
              link="/#"
              icon={
                <LogOut
                  style={{ height: 18, width: 18 }}
                  className="text-muted-foreground"
                />
              }
              menu="Log out"
              brandColor={companyBrandColor}
              liClassName="flex-1"
            />
            {["admin", "recruiter"].includes(userRole?.toLowerCase()) && (
              <div className="">
                <NavItem
                  onClick={() => {
                    handleNavigation("/notifications");
                  }}
                  link="/notifications"
                  icon={null}
                  menu={
                    <div className="relative text-muted-foreground">
                      <NotificationBell size={24} />
                    </div>
                  }
                  brandColor={companyBrandColor}
                  linkClassName="rounded-lg"
                />
              </div>
            )}
            {userRole?.toLowerCase() === "candidate" && (
              <div className="">
                <NavItem
                  onClick={() => handleNavigation("/candidate/notifications")}
                  link="/candidate/notifications"
                  icon={null}
                  menu={
                    <div className="relative text-muted-foreground">
                      <NotificationBell size={24} />
                    </div>
                  }
                  brandColor={companyBrandColor}
                  linkClassName="rounded-lg"
                />
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Use the reusable DataLossWarningDialog component */}
      <DataLossWarningDialog
        isOpen={showWarningDialog}
        onCancel={handleCancelNavigation}
        onConfirm={handleConfirmNavigation}
      />
    </>
  );
};

export default Sidebar;

/** Renders SVG assets at 18×18 and colors them with #A3A7AE. */
const SidebarAssetIcon = ({ src, alt = "" }) => (
  <div
    className="h-[18px] w-[18px] shrink-0"
    style={{
      backgroundImage: `url(${src})`,
      backgroundSize: "contain",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundColor: "transparent",
      // WebkitMask: `url(${src}) no-repeat center`,
      // mask: `url(${src}) no-repeat center`,
      // maskSize: "contain",
      // WebkitMaskSize: "contain",
    }}
    aria-hidden={alt ? undefined : true}
  />
);

// Icons as Components
const FormIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={18}
    height={18}
    viewBox="0 0 24 24"
  >
    <g fill="currentColor" className="text-muted-foreground">
      <path
        d="M16,4H2A2.006,2.006,0,0,0,0,6V24H18V6A2.006,2.006,0,0,0,16,4ZM8,22V18h2v4Zm8,0H12V17a.945.945,0,0,0-1-1H7a.945.945,0,0,0-1,1v5H2V6H16ZM8,10H6V8H8Zm4,0H10V8h2ZM8,14H6V12H8Zm4,0H10V12h2Z"
        transform="translate(3 -2)"
      />
    </g>
  </svg>
);

const LogoutIcon = () => (
  <svg width={18} height={18} viewBox="0 0 18 18" className="fill-current">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.4697 4.71967C11.7626 4.42678 12.2374 4.42678 12.5303 4.71967L16.2803 8.46967C16.5732 8.76256 16.5732 9.23744 16.2803 9.53033L12.5303 13.2803C12.2374 13.5732 11.7626 13.5732 11.4697 13.2803C11.1768 12.9874 11.1768 12.5126 11.4697 12.2197L14.6893 9L11.4697 5.78033C11.1768 5.48744 11.1768 5.01256 11.4697 4.71967Z"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6 9C6 8.58579 6.33579 8.25 6.75 8.25H15.75C16.1642 8.25 16.5 8.58579 16.5 9C16.5 9.41421 16.1642 9.75 15.75 9.75H6.75C6.33579 9.75 6 9.41421 6 9Z"
    />
  </svg>
);

const CompaniesIcon = () => (
  <svg
    width={18}
    height={20}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.66667 14.1668H13.3333M9.18141 2.30345L3.52949 6.69939C3.15168 6.99324 2.96278 7.14017 2.82669 7.32417C2.70614 7.48716 2.61633 7.67078 2.56169 7.866C2.5 8.08639 2.5 8.3257 2.5 8.80433V14.8334C2.5 15.7669 2.5 16.2336 2.68166 16.5901C2.84144 16.9037 3.09641 17.1587 3.41002 17.3185C3.76654 17.5001 4.23325 17.5001 5.16667 17.5001H14.8333C15.7668 17.5001 16.2335 17.5001 16.59 17.3185C16.9036 17.1587 17.1586 16.9037 17.3183 16.5901C17.5 16.2336 17.5 15.7669 17.5 14.8334V8.80433C17.5 8.3257 17.5 8.08639 17.4383 7.866C17.3837 7.67078 17.2939 7.48716 17.1733 7.32417C17.0372 7.14017 16.8483 6.99324 16.4705 6.69939L10.8186 2.30345C10.5258 2.07574 10.3794 1.96189 10.2178 1.91812C10.0752 1.87951 9.92484 1.87951 9.78221 1.91812C9.62057 1.96189 9.47418 2.07574 9.18141 2.30345Z"
      stroke="currentColor"
      strokeWidth="1.67"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CampaignsIcon = () => (
  <svg
    width={18}
    height={18}
    viewBox="0 0 17 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5.00169 10.835V12.5016M8.33502 7.50163V12.5016M11.6684 4.16829V12.5016M4.83502 15.835H11.835C13.2352 15.835 13.9352 15.835 14.47 15.5625C14.9404 15.3228 15.3229 14.9403 15.5625 14.4699C15.835 13.9352 15.835 13.2351 15.835 11.835V4.83496C15.835 3.43483 15.835 2.73476 15.5625 2.19998C15.3229 1.72958 14.9404 1.34713 14.47 1.10744C13.9352 0.834961 13.2352 0.834961 11.835 0.834961H4.83502C3.43489 0.834961 2.73483 0.834961 2.20005 1.10744C1.72964 1.34713 1.34719 1.72958 1.10751 2.19998C0.835022 2.73476 0.835022 3.43483 0.835022 4.83496V11.835C0.835022 13.2351 0.835022 13.9352 1.10751 14.4699C1.34719 14.9403 1.72964 15.3228 2.20005 15.5625C2.73483 15.835 3.43489 15.835 4.83502 15.835Z"
      stroke="currentColor"
      strokeWidth="1.67"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const VacanciesIcon = () => (
  <svg
    width={18}
    height={18}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M14.8333 8.33333C15.7668 8.33333 16.2335 8.33333 16.59 8.15168C16.9036 7.99189 17.1586 7.73692 17.3183 7.42332C17.5 7.0668 17.5 6.60009 17.5 5.66667V5.16667C17.5 4.23325 17.5 3.76654 17.3183 3.41002C17.1586 3.09641 16.9036 2.84145 16.59 2.68166C16.2335 2.5 15.7668 2.5 14.8333 2.5L5.16667 2.5C4.23325 2.5 3.76654 2.5 3.41002 2.68166C3.09641 2.84144 2.84144 3.09641 2.68166 3.41002C2.5 3.76654 2.5 4.23325 2.5 5.16667L2.5 5.66667C2.5 6.60009 2.5 7.0668 2.68166 7.42332C2.84144 7.73692 3.09641 7.99189 3.41002 8.15168C3.76654 8.33333 4.23325 8.33333 5.16667 8.33333L14.8333 8.33333Z"
      stroke="currentColor"
      strokeWidth="1.67"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14.8333 17.5C15.7668 17.5 16.2335 17.5 16.59 17.3183C16.9036 17.1586 17.1586 16.9036 17.3183 16.59C17.5 16.2335 17.5 15.7668 17.5 14.8333V14.3333C17.5 13.3999 17.5 12.9332 17.3183 12.5767C17.1586 12.2631 16.9036 12.0081 16.59 11.8483C16.2335 11.6667 15.7668 11.6667 14.8333 11.6667L5.16667 11.6667C4.23325 11.6667 3.76654 11.6667 3.41002 11.8483C3.09641 12.0081 2.84144 12.2631 2.68166 12.5767C2.5 12.9332 2.5 13.3999 2.5 14.3333L2.5 14.8333C2.5 15.7668 2.5 16.2335 2.68166 16.59C2.84144 16.9036 3.09641 17.1586 3.41002 17.3183C3.76654 17.5 4.23325 17.5 5.16667 17.5H14.8333Z"
      stroke="currentColor"
      strokeWidth="1.67"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Helper to darken a hex color by a percentage
function getHoverColor(hex, darkenPercent = 0.15) {
  if (!hex) return undefined;
  hex = hex.replace("#", "");
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((x) => x + x)
      .join("");
  }
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  // Darken each channel by the given percent
  r = Math.max(0, Math.floor(r * (1 - darkenPercent)));
  g = Math.max(0, Math.floor(g * (1 - darkenPercent)));
  b = Math.max(0, Math.floor(b * (1 - darkenPercent)));
  return `rgb(${r},${g},${b})`;
}

// Components
const NavItem = ({
  menu,
  link,
  icon,
  onClick,
  brandColor,
  liClassName = "",
  linkClassName = "",
}) => {
  const location = useLocation();
  const isActive = location.pathname === link;
  const [isHovered, setIsHovered] = React.useState(false);

  // Use brand color for company users, with hover effect
  const baseBg = brandColor
    ? {
        backgroundColor:
          isActive || isHovered ? getHoverColor(brandColor, 0.22) : brandColor,
        color: "#000",
      }
    : {};

  return (
    <li className={`relative list-none ${liClassName}`}>
      <a
        href={link}
        onClick={(e) => {
          e.preventDefault();
          if (onClick) onClick();
        }}
        className={`relative flex w-full items-center rounded py-[8px] my-[2px] px-[15px] text-base font-medium text-foreground transition-colors ${!brandColor ? (isActive ? "bg-muted" : "hover:bg-muted") : ""}
          ${linkClassName}`}
        style={baseBg}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {icon && (
          <span className="mr-[10px] text-muted-foreground flex items-center justify-center">
            {icon}
          </span>
        )}
        {menu}
      </a>
    </li>
  );
};

const DropdownItem = ({ link, menu, onClick }) => {
  const location = useLocation();
  const isActive = location.pathname === link;

  return (
    <li>
      <a
        href={link}
        onClick={(e) => {
          e.preventDefault();
          if (onClick) onClick();
        }}
        className={`flex w-full items-center rounded py-[8px] my-[2px] px-[15px] text-base font-medium ${
          isActive
            ? "text-foreground bg-muted"
            : "text-foreground text-opacity-50 hover:bg-muted hover:text-opacity-100 transition-colors"
        }`}
      >
        {menu}
      </a>
    </li>
  );
};
